import { ErrorMessage } from '@hookform/error-message';
import { TextFieldProps } from '@mui/material/TextField/TextField';
import {
  DateRange,
  DateRangePicker,
  MultiInputDateRangeField,
} from '@mui/x-date-pickers-pro';
import moment, { Moment } from 'moment/moment';
import { useCallback, useMemo } from 'react';
import * as React from 'react';
import {
  ControllerRenderProps,
  UseFormReturn,
  useController,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CollabTemplateFormType } from '~/scenes/PeopleModule/components/Tabs/CollaborationTab/WorklistTable/AddWorklistDialog/useAddWorklistDialog';
import { convertToDateOrUndefined } from '~/utils/helpers/date';

type PropsType = {
  form: UseFormReturn<CollabTemplateFormType>;
  disabled?: boolean;
  commonTextFieldProps?: Partial<TextFieldProps>;
  onResetTemplateTasks: (startDate?: Date, endDate?: Date) => void;
};

export const AddWorklistDateRange = (props: PropsType) => {
  const {
    form: {
      control,
      formState: { errors },
    },
    disabled,
    commonTextFieldProps,
    onResetTemplateTasks,
  } = props;

  const { t } = useTranslation();

  const {
    field: { value: startValue, onChange: onStartChange, ...startField },
  } = useController({
    name: `startDate`,
    control: control,
  });

  const {
    field: { value: endValue, onChange: onEndChange, ...endField },
  } = useController({
    name: `dueDate`,
    control: control,
  });

  const { values } = useMemo(() => {
    const formDateToFieldValue = (value?: Date): Moment | null =>
      !!value ? moment(value) : null;

    const values: DateRange<Moment> = [
      formDateToFieldValue(startValue),
      formDateToFieldValue(endValue),
    ];

    return {
      values,
    };
  }, [startValue, endValue]);

  const handleRangeChange = useCallback(
    (dateRange: DateRange<Moment>) => {
      const [startField, endField] = dateRange.map((fieldValue, index) => {
        const currentValue = values[index];

        return {
          hasChanged: fieldValue !== currentValue,
          date: convertToDateOrUndefined(fieldValue),
        };
      });

      if (startField.hasChanged) {
        onStartChange(startField.date);
      }
      if (endField.hasChanged) {
        onEndChange(endField.date);
      }
      if (startField.hasChanged || endField.hasChanged) {
        onResetTemplateTasks(startField.date, endField.date);
      }
    },
    [values, onStartChange, onEndChange, onResetTemplateTasks],
  );

  const getTextFieldProps = (
    fieldProps: Omit<
      ControllerRenderProps<CollabTemplateFormType>,
      'value' | 'onChange'
    >,
    label: string,
  ): TextFieldProps => {
    const hasFieldError = errors.hasOwnProperty(fieldProps.name);

    return {
      ...fieldProps,
      label,
      required: true,
      fullWidth: true,
      error: hasFieldError,
      helperText: hasFieldError && (
        <ErrorMessage errors={errors} name={fieldProps.name} />
      ),
      ...commonTextFieldProps,
    };
  };

  return (
    <DateRangePicker
      minDate={moment().add(1, 'day')}
      value={values}
      onChange={handleRangeChange}
      disabled={disabled}
      label={`${t('startDate')} - ${t('label_collaboration_completion_date')}`}
      slots={{
        field: (params) => (
          <MultiInputDateRangeField
            {...params}
            slotProps={{
              root: {
                sx: {
                  // Reduce spacing around separator
                  '& > :not(style) ~ :not(style)': {
                    marginLeft: '4px',
                  },
                },
              },
              textField: ({ position }) => {
                switch (position) {
                  case 'start':
                    return getTextFieldProps(startField, t('startDate'));
                  case 'end':
                    return getTextFieldProps(
                      endField,
                      t('label_collaboration_completion_date'),
                    );
                  default:
                    return {};
                }
              },
            }}
          />
        ),
      }}
    />
  );
};
